import React from 'react';
import {Box, Grid, Paper, Typography, Stack, Divider, useTheme, useMediaQuery} from '@mui/material';
import {BarChart} from '@mui/x-charts';
import {BarChart as BarChartIcon, Summarize, TrendingUp, TrendingDown, Percent} from '@mui/icons-material';

interface AylikGelirGiderTablosuProps {
    donemler: string[];
    aylikVeri: Record<string, any>;
    seciliDonem: string | undefined;
    setSeciliDonem: (val: string) => void;
    mevcutDonem: string;
}

const AylikGelirGiderTablosu: React.FC<AylikGelirGiderTablosuProps> = ({
                                                                           donemler,
                                                                           aylikVeri,
                                                                           seciliDonem,
                                                                           setSeciliDonem,
                                                                           mevcutDonem,
                                                                       }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const activeDonem = seciliDonem ?? mevcutDonem;
    const activeData = aylikVeri[activeDonem] || {toplamSatis: 0, toplamGider: 0, gun: 1};

    return (
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <BarChartIcon color="primary" />
                <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Aylık Performans (Son 12 Ay)</Typography>
            </Stack>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                    <Box sx={{ width: '100%', height: 350 }}>
                        <BarChart
                            onAxisClick={(event, d) => d?.axisValue && setSeciliDonem(d.axisValue as string)}
                            margin={{bottom: 40, left: 60, right: 20, top: 20}}
                            dataset={donemler.map((donem: string) => ({
                                ...aylikVeri[donem],
                                donem: donem,
                            }))}
                            xAxis={[{scaleType: 'band', dataKey: 'donem'}]}
                            series={[
                                {dataKey: 'toplamSatis', label: 'Satış', color: theme.palette.success.main},
                                {dataKey: 'toplamGider', label: 'Gider', color: theme.palette.error.main},
                            ]}
                            height={300}
                        />
                    </Box>
                </Grid>
                
                <Grid item xs={12} lg={5}>
                    <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: '16px', mb: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <Summarize sx={{ opacity: 0.8 }} />
                            <Typography variant="h6" fontWeight={800}>Dönem Özeti: {activeDonem}</Typography>
                        </Stack>
                        
                        <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 700 }}>Toplam Satış</Typography>
                                <Typography variant="h6" fontWeight={900}>{activeData.toplamSatis?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 700 }}>Toplam Gider</Typography>
                                <Typography variant="h6" fontWeight={900}>{activeData.toplamGider?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 700 }}>Günlük Ort. Satış</Typography>
                                <Typography variant="h6" fontWeight={900}>
                                    {(activeData.toplamSatis / (activeData.gun || 1))?.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺
                                </Typography>
                            </Box>
                            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 700 }}>Net Fark</Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    {(activeData.toplamSatis - activeData.toplamGider) >= 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                                    <Typography variant="h5" fontWeight={900}>
                                        {(activeData.toplamSatis - activeData.toplamGider)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: '12px' }}>
                                <Percent color="primary" sx={{ fontSize: 20, mb: 0.5 }} />
                                <Typography variant="caption" display="block" color="textSecondary" fontWeight={700}>Kar Oranı</Typography>
                                <Typography variant="h6" fontWeight={800}>
                                    {(activeData.toplamSatis / (activeData.toplamGider > 0 ? activeData.toplamGider : 1)).toFixed(2)}x
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={6}>
                            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: '12px' }}>
                                <TrendingUp color="primary" sx={{ fontSize: 20, mb: 0.5 }} />
                                <Typography variant="caption" display="block" color="textSecondary" fontWeight={700}>Günlük Ort.</Typography>
                                <Typography variant="h6" fontWeight={800}>
                                    {(activeData.toplamSatis / (activeData.gun || 1)).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default AylikGelirGiderTablosu;
